import React, { useEffect, useState } from "react"

import { A } from "../../../shared/utils/router"
import ContactPartnershipStyles from "./contact-partnership.module.css"
import FlexWrapper from "../../../shared/components/fields/flex-wrapper"
import IconAction from "../../../shared/components/universal/icon-action"
import Input from "../../../shared/components/fields/input"
import Radio from "../../../shared/components/fields/radio"
import ReactMarkdown from "react-markdown"
import Spacer from "../universal/spacer"
import Text from "../matrix/text"
import Textarea from "../../../shared/components/fields/textarea"
import { errorToast } from "../../../shared/utils/error-toast"
import { genders } from "../../../shared/config/genders"
import { scrollTo } from "../../../shared/utils/smooth-scroll"
import { useForm } from "react-hook-form"
import { usePostContact } from "../../../hooks/contact/use-query-contact"
import { useQueryParams } from "../../../shared/hooks/use-url-search-params"
import { useTranslation } from "react-i18next"

/* global exponea */

const ContactPartnershipShort = () => {
    const { t } = useTranslation("translations")

    const [isSuccess, setIsSuccess] = useState(false)

    useEffect(() => {
        if (isSuccess) {
            scrollTo("success_message")
        }
    }, [isSuccess])

    const mutate = usePostContact()

    const {
        handleSubmit,
        control,
        reset,
        register,
        formState: { errors },
    } = useForm({
        mode: "onTouched",
        defaultValues: {
            salutation: "",
            firstname: "",
            lastname: "",
            street: "",
            street_no: "",
            postcode: "",
            city: "",
            phone: "",
            email: "",
            job: "",
            fair: "",
            availability: "",
        },
        context: undefined,
        criteriaMode: "firstError",
        shouldFocusError: true,
    })

    const { getQueryParam } = useQueryParams()
    const utm_source = getQueryParam("utm_source")
    const utm_medium = getQueryParam("utm_medium")
    const utm_campaign = getQueryParam("utm_campaign")

    const submit = async values => {
        const body = {
            category_id: process.env.CONTACT_TRADE_FAIR_CATEGORY_ID,
            firstname: values.firstname,
            lastname: values.lastname,
            email: values.email,
            phone: values.phone,
            body: "",
        }

        body.body += t("contact_partnership_short_form_submit_body_1")

        if (values.salutation) {
            body.body += t("contact_partnership_short_form_submit_body_salutation", {
                salutation: t(genders.find(g => g.id === values.salutation).name),
            })
        }

        body.body += t("contact_partnership_short_form_submit_body_2", {
            firstname: values.firstname,
            lastname: values.lastname,
            street: values.street,
            street_no: values.street_no,
            postcode: values.postcode,
            city: values.city,
            job: values.job,
            phone: values.phone,
            email: values.email,
        })

        if (values.fair) {
            body.body += t("contact_partnership_short_form_submit_body_fair", {
                fair: values.fair,
            })
        }

        if (values.availability) {
            body.body += t("contact_partnership_short_form_submit_body_availability", {
                availability: values.availability,
            })
        }

        if (utm_source) {
            body.body += `\n\nutm_source: ${utm_source}\nutm_medium: ${utm_medium}\nutm_campaign: ${utm_campaign}`
        }

        try {
            await mutate.mutateAsync(body)
            setIsSuccess(true)
            if (exponea.isLoaded) {
                exponea.track("submit", { form: utm_campaign || "messekontakt" })
            }
            reset()
        } catch (error) {
            errorToast(error)
        }
    }

    return (
        <div className={ContactPartnershipStyles.wrapper} id="wrapper">
            {!isSuccess ? (
                <form
                    onSubmit={handleSubmit(submit)}
                    name="contact_partnership_short"
                    className={ContactPartnershipStyles.form}
                >
                    <h3>{t("contact_partnership_form_contact_details")}</h3>
                    <Radio
                        register={register("salutation")}
                        error={errors}
                        options={genders.map(g => ({
                            label: t(g.name),
                            value: g.id,
                        }))}
                        label={t("contact_partnership_form_salutation")}
                        flex
                    />
                    <FlexWrapper flex={["2 1 auto", "2 1 auto"]} wrap>
                        <Input
                            control={control}
                            rules={{ required: { value: true, message: t("form_error_required") } }}
                            type="text"
                            name="firstname"
                            label={t("contact_form_firstname_label")}
                            autocomplete="given-name"
                            required
                        />
                        <Input
                            control={control}
                            rules={{ required: { value: true, message: t("form_error_required") } }}
                            type="text"
                            name="lastname"
                            label={t("contact_form_lastname_label")}
                            autocomplete="family-name"
                            required
                        />
                    </FlexWrapper>
                    <FlexWrapper childWidths={[70, 30]}>
                        <Input
                            control={control}
                            rules={{ required: { value: true, message: t("form_error_required") } }}
                            type="text"
                            name="street"
                            label={t("contact_partnership_form_street")}
                            autocomplete="address-line1"
                            required
                        />
                        <Input
                            control={control}
                            rules={{ required: { value: true, message: t("form_error_required") } }}
                            type="text"
                            name="street_no"
                            label={t("contact_partnership_form_street_no")}
                            autoSize
                            required
                        />
                    </FlexWrapper>
                    <FlexWrapper childWidths={[30, 70]}>
                        <Input
                            control={control}
                            rules={{
                                required: { value: true, message: t("form_error_required") },
                            }}
                            type="text"
                            name="postcode"
                            label={t("contact_partnership_form_postcode")}
                            autocomplete="postal-code"
                            autoSize
                            required
                        />
                        <Input
                            control={control}
                            rules={{ required: { value: true, message: t("form_error_required") } }}
                            type="text"
                            name="city"
                            label={t("contact_partnership_form_city")}
                            autocomplete="address-level2"
                            required
                        />
                    </FlexWrapper>
                    <FlexWrapper flex={["2 1 auto", "2 1 auto"]} wrap>
                        <Input
                            rules={{
                                required: { value: true, message: t("form_error_required") },
                            }}
                            control={control}
                            type="tel"
                            name="phone"
                            autocomplete="tel"
                            label={t("contact_partnership_form_phone")}
                            required
                        />
                        <Input
                            control={control}
                            rules={{ required: { value: true, message: t("form_error_required") } }}
                            type="email"
                            name="email"
                            label={t("contact_partnership_form_email")}
                            autocomplete="email"
                            required
                        />
                    </FlexWrapper>
                    <Input
                        control={control}
                        rules={{ required: { value: true, message: t("form_error_required") } }}
                        type="text"
                        name="job"
                        label={t("contact_partnership_form_job")}
                        required
                        className={ContactPartnershipStyles.job}
                    />
                    <h3>{t("contact_partnership_short_form_fair")}</h3>
                    <Input
                        control={control}
                        type="text"
                        name="fair"
                        className={ContactPartnershipStyles.job}
                        placeholder={t("contact_partnership_short_form_fair_placeholder")}
                    />
                    <h3>{t("contact_partnership_short_form_availability_comments")}</h3>
                    <Textarea
                        control={control}
                        rules={{
                            required: { value: false },
                        }}
                        type="textarea"
                        name="availability"
                        length={400}
                        showLengthInfo
                    />
                    <Spacer small />
                    <div className={ContactPartnershipStyles.smallprint}>
                        <ReactMarkdown
                            components={{
                                a: ({ children, href }) => <A href={href}>{children}</A>,
                            }}
                        >
                            {t("contact_partnership_form_smallprint")}
                        </ReactMarkdown>
                    </div>
                    <p className={ContactPartnershipStyles.asterisk}>
                        <span style={{ color: "#bf4751" }}>* </span>
                        {t("form_required_field")}
                    </p>
                    <Spacer small />
                    <IconAction title={t("contact_partnership_short_form_button")} button submit />
                </form>
            ) : (
                <div className={ContactPartnershipStyles.success} id="success_message">
                    <div className={ContactPartnershipStyles.circle}>
                        <i className="lavita-icon"></i>
                    </div>
                    <Spacer small />
                    <Text
                        small
                        source={t("contact_partnership_form_success")}
                        align={{ desktop: "center", tablet: "center", mobile: "center" }}
                    />
                </div>
            )}
        </div>
    )
}

export default ContactPartnershipShort
